import * as React from 'react';
import { TeamStore as MobxTeamStore } from 'nomst/Stores/team.store';
import { inject, observer } from 'mobx-react';
import ManagerViewHeader from 'nomst/Components/Decision/managerViewHeader';
import OrgChart, { IOrgChartProps } from 'nomst/Components/Decision/orgChart';
import TeamDecision from 'nomst/Components/Decision/teamDecision';
import { HeaderView } from 'nomst/Stores/Decision/Constants/headerView';
import { Route, RouteComponentProps, Switch } from 'react-router-dom';
import OverBudgetDialog from 'nomst/Components/Decision/overBudgetDialog';
import WorkflowDialog from 'nomst/Components/Decision/workflowDialog';
import ManagerListSidePanel from 'nomst/Components/Decision/ManagerListSidePanel/managerListSidePanel';
import { Manager } from 'nomst/Stores/Decision/manager';
import { managerListSidePanelStore } from 'utils/MobxDataManager/singletons';
import { WorkflowAction } from 'nomst/Stores/Decision/Constants/workflowAction';
import './managerView.scss';

export interface IDecisionProps {
    store: MobxTeamStore;
}

export class Decision extends React.Component<IDecisionProps> {
    constructor(props: IDecisionProps) {
        super(props);

        this.renderOrgChart = this.renderOrgChart.bind(this);
        this.renderSidePanel = this.renderSidePanel.bind(this);
        this.renderTeamDecision = this.renderTeamDecision.bind(this);
        this.handleSubmitForReview = this.handleSubmitForReview.bind(this);
    }

    render(): React.ReactNode {
        // if manager list side panel is closed
        if (!managerListSidePanelStore.sidePanelListType) {
            return this.renderBody();
        }

        return (
            <div className="manager-list-open">
                <Switch>
                    <Route
                        path="/cycles/managers/managerview/orgchart"
                    />
                    <Route
                        path="/cycles/managers/managerview"
                        render={this.renderSidePanel}
                    />
                </Switch>
                <div className="manager-list-body">
                    {this.renderBody()}
                </div>
            </div>
        );
    }

    private renderSidePanel(): React.ReactNode {
        const { decisionStore, navigationStore, configuration } = this.props.store;
        return (
            <ManagerListSidePanel
                decisionDispatcher={decisionStore.decisionDispatcher}
                navigationStore={navigationStore}
                configuration={configuration}
                budgetNamesMap={decisionStore.budgetNames}
                managerWorkflowStore={decisionStore.managerWorkflowStore}
            />
        );
    }

    private renderBody(): React.ReactNode {
        const { user, decisionStore } = this.props.store;
        const { overBudgetStore, managerWorkflowStore, decisionDispatcher, teamDecisionStore } = decisionStore;
        return (
            <div className="manager-view">
                {this.renderHeader()}
                <Switch>
                    <Route
                        path="/cycles/managers/managerview/orgchart"
                        render={this.renderOrgChart}
                    />
                    <Route
                        path="/cycles/managers/managerview"
                        render={this.renderTeamDecision}
                    />
                </Switch>
                <OverBudgetDialog
                    overBudgetStore={overBudgetStore}
                    isHrUser={user.hasHrRole}
                    submitForReview={this.handleSubmitForReview}
                />
                <WorkflowDialog
                    decisionDispatcher={decisionDispatcher}
                    managerWorkflowStore={managerWorkflowStore}
                    teamView={teamDecisionStore.teamView}
                    currentUser={user}
                    isMultiManager={decisionStore.headerView === HeaderView.MultiManagerHeader}
                />
            </div>
        );
    }

    private renderHeader(): React.ReactNode {
        const { user, decisionStore, navigationStore, configuration } = this.props.store;
        const multiManager = decisionStore.headerView === HeaderView.MultiManagerHeader;

        const {
            managerHeaderStore,
            managerWorkflowStore,
            decisionDispatcher } = decisionStore;

        const manager = managerHeaderStore && managerHeaderStore.managers &&
            managerHeaderStore.managers.length ? managerHeaderStore.managers[0] : null;

        const selectedManagerIds: string[] = managerListSidePanelStore.selectedManagerIds;

        return (
            <ManagerViewHeader
                decisionDispatcher={decisionDispatcher}
                navigationStore={navigationStore}
                manager={manager}
                managerWorkflowStore={managerWorkflowStore}
                currentUser={user}
                multiManager={multiManager}
                managersCount={selectedManagerIds.length}
                firstManagerSourceId={selectedManagerIds[0]}
                configuration={configuration}
                teamDecisionStore={decisionStore.teamDecisionStore}
            />
        );
    }

    private renderOrgChart(orgChartProps: RouteComponentProps<IOrgChartProps>): React.ReactNode {
        const { navigationStore, configuration } = this.props.store;
        return (
            <OrgChart
                url={orgChartProps.location.search}
                navigationStore={navigationStore}
                configuration={configuration}
            />
        );
    }

    private renderTeamDecision(): React.ReactNode {
        const { configuration, decisionStore, user, navigationStore } = this.props.store;
        const { managerHeaderStore } = decisionStore;
        const managers = managerHeaderStore.managers || [];
        const managerIds = managers.map((manager: Manager) => manager.employeeSourceId);
        const directReportsCount = managers.length > 0
            ? managerHeaderStore.selectedManagersDirectReportsCount
            : 0;
        // Only need all reports count when a single manager is selected
        const allReportsCount = managers.length === 1
            ? managers[0].allReportsCount
            : 0;

        const shouldHideChangeHistory = decisionStore.headerView === HeaderView.MultiManagerHeader;
        return (
            <TeamDecision
                teamDecisionStore={decisionStore.teamDecisionStore}
                decisionDispatcher={decisionStore.decisionDispatcher}
                configuration={configuration}
                user={user}
                currentManagerIds={managerIds}
                directReportsCount={directReportsCount}
                allReportsCount={allReportsCount}
                navigationStore={navigationStore}
                workflowStatus={managerListSidePanelStore.workflowStatusType}
                budgetCycleId={managerListSidePanelStore.budgetCycleId}
                shouldHideChangeHistory={shouldHideChangeHistory}
                managerWorkflowStore={decisionStore.managerWorkflowStore}
            />
        );
    }

    private handleSubmitForReview(): void {
        const { decisionDispatcher } = this.props.store.decisionStore;
        decisionDispatcher.dispatchWorkflowAction(WorkflowAction.SubmitReview);
    }
}

export default inject('store')(observer(Decision));